import React, { useEffect, useRef } from 'react';
import {
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import * as z from 'zod';

import InputField from '@/components/atoms/InputField';
import BudgetMembersList from '@/components/organisms/budget/BudgetMembersList';
import { Button } from '@/components/ui/button';
import { BudgetFormValues } from './budgetStep.schema';

import isMobile from 'is-mobile';

interface MembersStepProps {
  register: UseFormRegister<BudgetFormValues>;
  watch: UseFormWatch<BudgetFormValues>;
  setValue: UseFormSetValue<BudgetFormValues>;
  errors: FieldErrors<BudgetFormValues>;
  fields: FieldArrayWithId<BudgetFormValues, 'members', 'id'>[];
  remove: UseFieldArrayRemove;
  onInviteMember: () => Promise<void>;
  isInvitingMember?: boolean;
  setError: UseFormSetError<BudgetFormValues>;
  clearErrors: UseFormClearErrors<BudgetFormValues>;
  currentUserEmail?: string;
  currentUserPhotoUrl?: string;
  currency?: string;
}

const MembersStep: React.FC<MembersStepProps> = ({
  register,
  watch,
  errors,
  setValue,
  fields,
  remove,
  onInviteMember,
  isInvitingMember = false,
  setError,
  clearErrors,
  currentUserEmail,
  currentUserPhotoUrl,
  currency = 'EUR'
}) => {
  const inviteEmail = watch('inviteEmail');
  const prevInviteEmailRef = useRef(inviteEmail);

  // Clear invite error only when the user edits the email field, not when an error is set.
  useEffect(() => {
    if (prevInviteEmailRef.current !== inviteEmail) {
      clearErrors('inviteEmail');
      prevInviteEmailRef.current = inviteEmail;
    }
  }, [inviteEmail, clearErrors]);

  const handleInviteClick = async () => {
    const email = inviteEmail?.trim();

    clearErrors('inviteEmail');

    if (!email) {
      setError('inviteEmail', { type: 'manual', message: 'Enter an email address' });
      return;
    }

    if (!z.string().email().safeParse(email).success) {
      setError('inviteEmail', { type: 'manual', message: 'Invalid email address' });
      return;
    }

    if (currentUserEmail && email.toLowerCase() === currentUserEmail.toLowerCase()) {
      setError('inviteEmail', { type: 'manual', message: 'You are already the budget owner' });
      return;
    }

    const members = watch('members') ?? [];
    if (members.some((member) => member.email.toLowerCase() === email.toLowerCase())) {
      setError('inviteEmail', { type: 'manual', message: 'This member is already in the list' });
      return;
    }

    await onInviteMember();
  };

  return (
    <div className="space-y-4">
      <BudgetMembersList
        variant="create"
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        fields={fields}
        remove={remove}
        currentUserEmail={currentUserEmail}
        currentUserPhotoUrl={currentUserPhotoUrl}
        currency={currency}
      />

      <div className="flex items-start gap-2 !mt-8">
        <div className="flex-1">
          <InputField
            name="inviteEmail"
            placeholder={
              isMobile() ? 'Enter email address...' : 'Enter email address of the member you want to invite...'
            }
            type="email"
            register={register}
            error={errors.inviteEmail}
            size="sm"
            disabled={isInvitingMember}
          />
        </div>
        <Button
          variant="text"
          size="sm"
          className="text-lg font-semibold shrink-0 mt-1"
          type="button"
          onClick={handleInviteClick}
          disabled={isInvitingMember}
        >
          {isInvitingMember ? 'Inviting...' : 'Invite'}
        </Button>
      </div>
    </div>
  );
};

export default MembersStep;
